import { useEffect, useMemo } from "react";

import { useMaterialFormulas } from "../../../hooks/query";
import { calculateMaterialQuantities } from "../utils/calculateMaterialQuantities";
import type { MaterialFormulaFlag } from "../types";
import type { MaterialRowItem } from "../utils/materialLineItems";
import { useCalculationScope } from "./useCalculationScope";
import { useEstimateContractContextManager } from "./";

export function useMaterialEvaluator(
    materialsByGroup: Record<string, MaterialRowItem[]>,
) {
    const { values, setValues } = useEstimateContractContextManager();
    const { scope, loading: scopeLoading } = useCalculationScope(materialsByGroup);
    const { useList } = useMaterialFormulas();
    const {
        data: formulas,
        isLoading: formulasLoading,
        isError,
        error,
    } = useList();

    const ready = !scopeLoading && !formulasLoading && !!formulas;

    const { quantities, lineQuantities, byCategory, formulaInfo } = useMemo(() => {
        if (!ready) {
            return {
                quantities: {},
                lineQuantities: {},
                byCategory: {},
                formulaInfo: {},
            };
        }
        return calculateMaterialQuantities(
            scope,
            formulas,
            values.materials,
            materialsByGroup,
        );
    }, [ready, scope, formulas, values.materials, materialsByGroup]);

    const syncMaterialQuantities = () => {
        if (
            !Object.keys(quantities).length &&
            !Object.keys(lineQuantities).length
        ) {
            return;
        }

        setValues((prev: any) => {
            const materials = { ...prev.materials };
            let changed = false;

            for (const [categoryName, qty] of Object.entries(quantities)) {
                for (const editKey of byCategory[categoryName] || []) {
                    const current = materials[editKey];
                    if (!current || current.quantityOverridden) continue;

                    const nextQty =
                        lineQuantities[editKey] != null
                            ? Number(lineQuantities[editKey])
                            : Number(qty);

                    if (Number(current.quantity) === nextQty) continue;

                    changed = true;
                    materials[editKey] = {
                        ...current,
                        quantity: nextQty,
                    };
                }
            }

            return changed ? { ...prev, materials } : prev;
        });
    };

    useEffect(() => {
        syncMaterialQuantities();
    }, [quantities, lineQuantities, byCategory, setValues]);

    return {
        quantities,
        formulaFlags: formulaInfo as Record<string, MaterialFormulaFlag>,
        ready,
        loading: scopeLoading || formulasLoading,
        error: isError
            ? (error as Error)?.message || "Failed to load material formulas"
            : null,
    };
}
